import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  Download,
  Trash2,
  Edit3,
  Info,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Eye,
  User,
  Calendar,
} from 'lucide-react';
import { getSupabaseAdminClient, GDPRRequest } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

type RequestType = 'all' | 'data_export' | 'data_deletion' | 'data_correction' | 'info_request';
type RequestStatus = 'all' | 'pending' | 'in_progress' | 'completed' | 'rejected';

export function GDPRPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<RequestType>('all');
  const [statusFilter, setStatusFilter] = useState<RequestStatus>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState<GDPRRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const pageSize = 10;
  const queryClient = useQueryClient();

  // Fetch GDPR requests with pagination
  const { data: requestsData, isLoading } = useQuery({
    queryKey: ['gdpr-requests', currentPage, searchQuery, typeFilter, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('gdpr_requests')
        .select('*', { count: 'exact' });

      // Apply search filter
      if (searchQuery) {
        query = query.ilike('user_email', `%${searchQuery}%`);
      }

      // Apply type filter
      if (typeFilter !== 'all') {
        query = query.eq('request_type', typeFilter);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        items: data as GDPRRequest[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['gdpr-stats'],
    queryFn: async () => {
      const [pendingResult, inProgressResult, urgentResult, completedResult] = await Promise.all([
        supabase.from('gdpr_requests').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('gdpr_requests').select('id', { count: 'exact', head: true }).eq('status', 'in_progress'),
        // Urgent: pending requests with deadline within 7 days
        supabase.from('gdpr_requests').select('id', { count: 'exact', head: true })
          .eq('status', 'pending')
          .lte('deadline_at', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()),
        supabase.from('gdpr_requests').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
      ]);

      return {
        pending: pendingResult.count || 0,
        inProgress: inProgressResult.count || 0,
        urgent: urgentResult.count || 0,
        completed: completedResult.count || 0,
      };
    },
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const updates: Record<string, unknown> = { status };
      if (notes !== undefined) {
        updates.admin_notes = notes;
      }
      if (status === 'completed' || status === 'rejected') {
        updates.processed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('gdpr_requests')
        .update(updates as never)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gdpr-requests'] });
      queryClient.invalidateQueries({ queryKey: ['gdpr-stats'] });
      toast.success('Status aktualisiert');
      setSelectedRequest(null);
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  const totalPages = Math.ceil((requestsData?.totalCount || 0) / pageSize);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'data_export':
        return <Download className="w-4 h-4 text-blue-500" />;
      case 'data_deletion':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'data_correction':
        return <Edit3 className="w-4 h-4 text-amber-500" />;
      case 'info_request':
        return <Info className="w-4 h-4 text-purple-500" />;
      default:
        return <Shield className="w-4 h-4 text-slate-500" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, { label: string; bg: string; text: string }> = {
      data_export: { label: 'Datenexport', bg: 'bg-blue-100', text: 'text-blue-700' },
      data_deletion: { label: 'Löschung', bg: 'bg-red-100', text: 'text-red-700' },
      data_correction: { label: 'Korrektur', bg: 'bg-amber-100', text: 'text-amber-700' },
      info_request: { label: 'Auskunft', bg: 'bg-purple-100', text: 'text-purple-700' },
    };
    const config = labels[type] || { label: type, bg: 'bg-slate-100', text: 'text-slate-700' };
    return (
      <span className={`px-2 py-1 ${config.bg} ${config.text} text-xs rounded-full`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            Ausstehend
          </span>
        );
      case 'in_progress':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
            <Clock className="w-3 h-3" />
            In Bearbeitung
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            Erledigt
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
            <XCircle className="w-3 h-3" />
            Abgelehnt
          </span>
        );
      default:
        return null;
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">DSGVO-Anfragen</h1>
        <p className="text-slate-600">Datenschutz-Anfragen bearbeiten (30 Tage Frist)</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.pending || 0}</div>
              <div className="text-sm text-slate-600">Ausstehend</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.inProgress || 0}</div>
              <div className="text-sm text-slate-600">In Bearbeitung</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.urgent || 0}</div>
              <div className="text-sm text-slate-600">Dringend (&lt;7 Tage)</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.completed || 0}</div>
              <div className="text-sm text-slate-600">Erledigt</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nach E-Mail suchen..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
            </div>
          </form>
          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as RequestType);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Typen</option>
              <option value="data_export">Datenexport</option>
              <option value="data_deletion">Löschung</option>
              <option value="data_correction">Korrektur</option>
              <option value="info_request">Auskunft</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as RequestStatus);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="all">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="in_progress">In Bearbeitung</option>
              <option value="completed">Erledigt</option>
              <option value="rejected">Abgelehnt</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Typ</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">E-Mail</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Eingegangen</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Frist</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Lädt...
                  </td>
                </tr>
              ) : requestsData?.items?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Keine Anfragen gefunden
                  </td>
                </tr>
              ) : (
                requestsData?.items?.map((request) => {
                  const daysLeft = getDaysUntilDeadline(request.deadline_at);
                  const isUrgent = daysLeft <= 7 && request.status !== 'completed' && request.status !== 'rejected';

                  return (
                    <tr key={request.id} className={`hover:bg-slate-50 ${isUrgent ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(request.request_type)}
                          {getTypeBadge(request.request_type)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-900">{request.user_email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{formatDate(request.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className={`text-sm ${isUrgent ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                            {formatDate(request.deadline_at)}
                            {isUrgent && ` (${daysLeft} Tage)`}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setAdminNotes(request.admin_notes || '');
                            }}
                            className="p-1 hover:bg-slate-100 rounded"
                            title="Details & Bearbeiten"
                          >
                            <Eye className="w-4 h-4 text-slate-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, requestsData?.totalCount || 0)} von {requestsData?.totalCount?.toLocaleString('de-DE') || 0}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 border border-slate-300 rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'hover:bg-slate-100'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getTypeIcon(selectedRequest.request_type)}
                  <h2 className="text-xl font-semibold text-slate-900">DSGVO-Anfrage</h2>
                </div>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-500">E-Mail</div>
                  <div className="font-medium text-slate-900">{selectedRequest.user_email}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Typ</div>
                  <div className="mt-1">{getTypeBadge(selectedRequest.request_type)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Eingegangen</div>
                  <div className="font-medium text-slate-900">{formatDate(selectedRequest.created_at)}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500">Frist</div>
                  <div className="font-medium text-slate-900">{formatDate(selectedRequest.deadline_at)}</div>
                </div>
              </div>
              {selectedRequest.description && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Beschreibung</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>
              )}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Admin-Notizen</h3>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  rows={3}
                  placeholder="Notizen zur Bearbeitung..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedRequest.id,
                    status: 'in_progress',
                    notes: adminNotes,
                  })}
                  disabled={selectedRequest.status === 'in_progress'}
                  className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 disabled:opacity-50"
                >
                  In Bearbeitung
                </button>
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedRequest.id,
                    status: 'completed',
                    notes: adminNotes,
                  })}
                  disabled={selectedRequest.status === 'completed'}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 disabled:opacity-50"
                >
                  Erledigt
                </button>
                <button
                  onClick={() => updateStatusMutation.mutate({
                    id: selectedRequest.id,
                    status: 'rejected',
                    notes: adminNotes,
                  })}
                  disabled={selectedRequest.status === 'rejected'}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
                >
                  Ablehnen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
